import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay, timeout } from 'rxjs/operators';

type PreviewData = {
  title: string | null;
  url: string;
  source: string | null;
  image_url: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class CommunityService {
  API_SERVER = environment.apiUrl;
  API_SERVER_SECURED = `${environment.apiUrl}/secured`;

  private previewCache = new Map<string, PreviewData>();
  private previewInflight = new Map<string, Observable<PreviewData>>();

  constructor(
    private httpClient: HttpClient,
    private toastrService: ToastrService,
  ) {}

  getCommunityBySlug(slug: string): Observable<any> {
    return this.httpClient
      .get(`${this.API_SERVER}/communities/${encodeURIComponent(slug)}`)
      .pipe(map((res: any) => this.extractData(res)));
  }

  getCommunityByIdPublic(id: number): Observable<any> {
    return this.httpClient
      .get(`${this.API_SERVER}/communities/id/${id}`)
      .pipe(map((res: any) => this.extractData(res)));
  }

  resolvePostByUuid(uuid: string) {
    return this.httpClient.get(`${this.API_SERVER}/communities/resolve/post/${encodeURIComponent(uuid)}`);
  }

  getCommunityStats(communityID: number): Observable<any> {
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/${communityID}/stats`)
      .pipe(map((res: any) => this.extractData(res)));
  }

  createPost(communityID: number, fd: FormData, cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .post(`${this.API_SERVER_SECURED}/communities/${communityID}/posts`, fd, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  updatePost(communityID: number, postID: number, fd: FormData, cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .patch(`${this.API_SERVER_SECURED}/communities/${communityID}/posts/${postID}`, fd, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  deletePost(communityID: number, postID: number, cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .delete(`${this.API_SERVER_SECURED}/communities/${communityID}/posts/${postID}`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  getCommunityPosts(
    communityID: number,
    cParams: { cursor?: number; limit?: number } = {}
  ): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/${communityID}/posts`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  getReplies(communityID: number, postId: number): Observable<any> {
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/${communityID}/posts/${postId}/replies`)
      .pipe(map((res: any) => this.extractData(res)));
  }

  getUserCommunityPosts(communityID: number, cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/${communityID}/my-posts`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  getUserPublicProfile(communityID: number, userID: number, cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/${communityID}/user-profile/${userID}`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  updateProfileVisibilaty(communityID: number, data: any): Observable<any> {
    return this.httpClient
      .patch(`${this.API_SERVER_SECURED}/communities/${communityID}/profile-visibility`, data)
      .pipe(map((res: any) => this.extractData(res)));
  }

  getProfileVisibilaty(communityID: number, cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/${communityID}/profile-visibility`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  getCommunityUsers(communityID: number, cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/${communityID}/users`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  getRecruiterStatus(cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/recruiter-status`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  getjobApplicants(communityID: number, postID: number, cParams = {}): Observable<any> {
    const params = this.getHttpParams(cParams);
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/${communityID}/${postID}/applicants`, { params })
      .pipe(map((res: any) => this.extractData(res)));
  }

  getLinkPreview(url: string): Observable<PreviewData> {
    const params = new HttpParams().set('url', (url || '').trim());
    return this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/link-preview`, { params })
      .pipe(
        map((res: any) => (res?.data as PreviewData) || {
          title: null, url, source: this.domainOf(url), image_url: null
        }),
        catchError(() => of({
          title: null, url, source: this.domainOf(url), image_url: null
        }))
      );
  }

  getLinkPreviewCached(url: string): Observable<PreviewData> {
    const key = (url || '').trim();
    if (!key) {
      return of({ title: null, url: '', source: null, image_url: null });
    }
    const hit = this.previewCache.get(key);
    if (hit) return of(hit);
    const inflight = this.previewInflight.get(key);
    if (inflight) return inflight;

    const params = new HttpParams().set('url', key);
    const req$ = this.httpClient
      .get(`${this.API_SERVER_SECURED}/communities/link-preview`, { params })
      .pipe(
        timeout({ each: 5000 }),
        map((res: any) => {
          const data = (res?.data as Partial<PreviewData>) || {};
          const normalized: PreviewData = {
            title: (data.title ?? null) as any,
            url:   (data.url   ?? key)  as any,
            source:(data.source?? this.domainOf(data.url ?? key)) as any,
            image_url: (data.image_url ?? null) as any,
          };
          return normalized;
        }),
        catchError(() =>
          of<PreviewData>({
            title: null, url: key, source: this.domainOf(key), image_url: null
          })
        ),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    req$.subscribe({
      next: (val) => { this.previewCache.set(key, val); this.previewInflight.delete(key); },
      error: ()     => { this.previewInflight.delete(key); }
    });

    this.previewInflight.set(key, req$);
    return req$;
  }

  clearPreviewCache() { this.previewCache.clear(); }
  dropPreview(url: string) { this.previewCache.delete((url || '').trim()); }

  private domainOf(u?: string): string {
    if (!u) return '';
    try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return ''; }
  }

  private extractData(res: Response): any {
    const body: any = res;
    return body || {};
  }

  private getHttpParams(cParams: any): HttpParams {
    let params = new HttpParams();
    Object.keys(cParams).forEach((k) => {
      params = params.set(k, cParams[k]);
    });
    return params;
  }
}
